import axios from "axios";
import tokenInterceptor from "$api/tokenInterceptor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import dayjs from "dayjs";

export let avagClient;

export const configureClient = (url) => {
  avagClient = axios.create({
    baseURL: url,
  });

  // add token to all requests
  avagClient.interceptors.request.use(tokenInterceptor);
};

const preparePath = (crm_dealercode, date, team, source) => {
  let path = "";
  if (source === "") {
    path = `?crm_dealercode=${crm_dealercode}&date=${date}&team=${team}`;
  } else {
    path = `?crm_dealercode=${crm_dealercode}&date=${date}&team=${team}&source=${source}`;
  }
  return path;
};

// prepare time slots

const prepareTimeSlots = (time_slots) => {
  let items_dict = {};
  time_slots.forEach((slot, index) => {
    const date = dayjs(slot.date).format("YYYY-MM-DD");
    if (!items_dict[date]) items_dict[date] = [];
    items_dict[dayjs(slot.date).format("YYYY-MM-DD")].push(slot);
  });
  return items_dict;
};

export const fetchTimeSlotsQuery = async ({
  queryKey: [_, locationId, acceptHeader = "application/json"],
}) => {
  let res;
  try {
    res = await avagClient.get(`/slots/?location_id=${locationId}`, {
      headers: {
        accept: acceptHeader,
      },
    });
  } catch (err) {
    console.error(err.response);
    return [];
  }
  return prepareTimeSlots(res?.data.location_slots);
};

export const fetchCarHouses = async ({
  queryKey: [_, acceptHeader = "application/json"],
}) => {
  const res = await avagClient.get("/car_houses/", {
    headers: {
      accept: acceptHeader,
    },
  });

  return res?.data.car_houses;
};

export const fetchLocations = async ({
  queryKey: [_, acceptHeader = "application/json"],
}) => {
  let res;
  try {
    res = await avagClient.get("/locations/", {
      headers: {
        accept: acceptHeader,
      },
    });
  } catch (error) {
    console.error(error.response);
    return [];
  }
  return res?.data.locations;
};

const prepareBody = (body) => {
  let { datePicker, timePicker, natCode, prefixCode, number, ...bodyData } = {
    ...body,
  };
  let date = datePicker
    .hour(timePicker.substr(0, 2))
    .minute(timePicker.substr(3, 2));
  bodyData["date"] = date.format("YYYY-MM-DDTHH:mm");
  return bodyData;
};

export const useCreateBooking = (location) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      if (Object.keys(body).includes("date")) {
        return await avagClient.post(
          "/bookings/?source=Backoffice",
          JSON.stringify(body),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        const postBody = prepareBody(body);
        return await avagClient.post(
          "/bookings/?source=Backoffice",
          JSON.stringify(postBody),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["bookedTimeSlots", location]);
      notification.success({
        message: "Erfolg",
        description: "Termin wurde gebucht!",
      });
    },
    onError: (error) => {
      console.log(error);
      queryClient.invalidateQueries(["bookedTimeSlots", location]);
      notification.error({
        message: "Fehler!",
        description: "Termin konnte nicht erstellt werden",
      });
    },
  });
};

export const useUpdateBooking = (crm_dealercode, date, team) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const putBody = prepareBody(body);
      console.log("Trying to update");
      return await avagClient.put(
        `bookings/${preparePath(crm_dealercode, date, team, "Backoffice")}`,
        JSON.stringify(putBody),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        "bookedTimeSlots",
        crm_dealercode,
        date,
        team,
      ]);
      notification.success({
        message: "Erfolg",
        description: "Termin wurde umgebucht!",
      });
    },
    onError: () => {
      queryClient.invalidateQueries([
        "bookedTimeSlots",
        crm_dealercode,
        date,
        team,
      ]);
      notification.error({
        message: "Fehler!",
        description: "Termin konnte nicht umgebucht werden",
      });
    },
  });
};

export const useDeleteBooking = (crm_dealercode, date, team) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await avagClient.delete(
        `bookings/${preparePath(crm_dealercode, date, team, "Backoffice")}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["timeSlots", crm_dealercode]);
      notification.success({
        message: "Erfolg",
        description: "Termin wurde gelöscht!",
      });
    },
    onError: () => {
      queryClient.invalidateQueries(["timeSlots", crm_dealercode]);
      notification.error({
        message: "Fehler!",
        description: "Termin konnte nicht gelöscht werden",
      });
    },
  });
};

export const fetchAppointsAsExcel = async (date) => {
  let result;
  try {
    result = await avagClient.get(`bookings/appointments?input_date=${date}`, {
      responseType: "blob",
    });
  } catch (error) {
    notification.error({ message: "Keine Termine zu exportieren!" });
    return;
  }

  const url = window.URL.createObjectURL(result.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `appointments_${date}.xlsx`);
  document.body.appendChild(link);

  link.click();
  link.parentNode.removeChild(link);
};

export const fetchBookedSlot = async ({
  queryKey: [_, crm_dealercode, date, team, acceptHeader = "application/json"],
}) => {
  let res;
  try {
    const path = `/bookings/single${preparePath(crm_dealercode, date, team)}`;
    res = await avagClient.get(path, {
      headers: {
        accept: acceptHeader,
      },
    });
  } catch (error) {
    console.error(error.response);
    return [];
  }
  return res?.data.booking;
};
